import React, { useEffect, useMemo, useRef, useState } from "react";
import "./TablePagination.scss";
import { SelectInput, IconButton, TextInput } from "../../../controls";
import classnames from "classnames";
import { formatNumber } from "../../../../utils";
import { useDebounce } from "react-use";
import {
  PageSizeOption,
  TABLE_PAGE_SIZE_OPTIONS,
} from "../tablePageSizeOptions";

export const DEBOUNCE_TIME = 800;

export interface TablePaginationProps {
  totalCount: number;
  loadMoreByPage?: (pageSize: number, pageIndex: number) => void;
  shouldResetPagination?: boolean;
  resetSelection?: () => void;
  pageSizeEnabled?: boolean;
  pageSizeOptionsList?: PageSizeOption[] | null;
  pageIndex: number;
  setPageIndex: (pageIndex: number) => void;
  pageSize: number;
  // Only invoked from the page-size selector, which renders only when
  // pageSizeEnabled — consumers that disable page size may omit it.
  setPageSize?: (pageSize: number) => void;
  loading?: boolean;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  totalCount,
  loadMoreByPage,
  shouldResetPagination,
  resetSelection,
  pageSizeEnabled = true,
  pageSizeOptionsList = null,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
}) => {
  // if the user inserts invalid page number - I can't change the page index (used outside) but do need to show the user what he typed
  // so I need this "internal" page index for view only.
  const [pageIndexViewOnly, setPageIndexViewOnly] = useState(pageIndex);
  const totalCountOfPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = pageIndex < totalCountOfPages;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadMoreByPage?.(pageSize, pageIndex);
  }, [pageSize, pageIndex, loadMoreByPage]);

  const [, cancel] = useDebounce(
    () => {
      if (!!pageIndexViewOnly && pageIndexViewOnly <= totalCountOfPages) {
        // if the user typed a valida number - we need to call the load more by page
        // and set all the page indices (originalPageIndex, pageIndex, pageIndexViewOnly)
        setPageIndex(pageIndexViewOnly);
        inputRef?.current?.blur();
      }
    },
    DEBOUNCE_TIME,
    [pageIndexViewOnly, loadMoreByPage]
  );

  const setPageIndexAndCancelDebounce = (pageIndex: number) => {
    setPageIndex(pageIndex);
    cancel();
  };

  useEffect(() => {
    if (!!shouldResetPagination) {
      setPageIndexViewOnly(1);
      setPageIndexAndCancelDebounce(1);
      resetSelection?.();
    }
  }, [shouldResetPagination]);

  const pageSizeOptions = useMemo(() => {
    return pageSizeOptionsList || [...TABLE_PAGE_SIZE_OPTIONS];
  }, [pageSizeOptionsList]);

  return (
    <>
      {!!totalCount && (
        <div
          className={`table-pagination ${pageSizeEnabled ? "with-page-size" : ""}`}
        >
          {pageSizeEnabled && (
            <div
              className="displayed-count-selection"
              onClick={(e) => e.stopPropagation()}
            >
              <label className={"select-page-size-label"}>Rows per page</label>
              <SelectInput
                className={"select-page-size no-padding-bottom"}
                data-testid={"row-count-select"}
                name={"select-row-count"}
                value={pageSizeOptions.find((el) => el.value === pageSize)}
                onChange={(selectedValue: PageSizeOption) => {
                  setPageSize?.(selectedValue?.value);
                  setPageIndexAndCancelDebounce(1);
                  setPageIndexViewOnly(1);
                  resetSelection?.();
                }}
                options={pageSizeOptions}
                defaultValue={pageSizeOptions[0]}
                size={"custom"}
                noMargin
              />
            </div>
          )}
          <div className="pagination-actions">
            <div
              className={classnames("pagination-item arrow-item ", {
                disabled: pageIndex === 1,
              })}
            >
              <IconButton
                isIcon={"seem-icon-left-arrow"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPageIndexViewOnly(pageIndex - 1);
                  setPageIndexAndCancelDebounce(pageIndex - 1);
                }}
                type={"button"}
                disabled={pageIndex === 1}
                size={"xs"}
                isSecondary
                data-testid={"previous-page-button"}
              />
            </div>
            <TextInput
              className="pagination-input no-padding-bottom"
              data-testid={"pagination"}
              type="text" //onlyDigits is not working with type="number"
              onlyDigits
              hideError
              error={
                !pageIndexViewOnly ||
                !totalCountOfPages ||
                pageIndexViewOnly > totalCountOfPages
              }
              inputRef={inputRef}
              value={pageIndexViewOnly}
              onChange={(e) => {
                setPageIndexViewOnly(parseInt(e.target.value));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const parsedIndex = parseInt(
                    (e.target as HTMLInputElement).value
                  );
                  if (parsedIndex <= totalCountOfPages) {
                    setPageIndexAndCancelDebounce(parsedIndex);
                  }
                }
              }}
              onBlur={() => {
                if (
                  pageIndexViewOnly > totalCountOfPages ||
                  !pageIndexViewOnly
                ) {
                  setPageIndexViewOnly(pageIndex);
                }
              }}
              disabled={totalCountOfPages === 1}
            />
            <span className="pagination-of">
              of {formatNumber(totalCountOfPages)}
            </span>
            <div
              className={classnames("pagination-item arrow-item", {
                disabled: !hasNextPage,
              })}
            >
              <IconButton
                isIcon={"seem-icon-right-arrow"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPageIndexAndCancelDebounce(pageIndex + 1);
                  setPageIndexViewOnly(pageIndex + 1);
                }}
                type={"button"}
                disabled={!hasNextPage}
                size={"xs"}
                isSecondary
                data-testid={"next-page-button"}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TablePagination;
